import { useEffect, useRef, useState } from "react"
import classNames from "classnames"
import dayjs, { Dayjs } from "dayjs"

import { ReactComponent as IconCalendar } from "assets/svg/icon-calendar.svg"
import { ReactComponent as IconTableCross } from "assets/svg/icon-table-cross.svg"

import { stringifyDateRange } from "utils/dateUtils"
import { useOutsideClick } from "utils/useOutsideClick"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import DateRangePicker from "modals/StatsReportModal/DateRangePicker"

import styles from "./index.module.scss"

interface Props {
  label?: string
  name?: string
  dateRangeParamName?: string
}

export const TableDateRangePicker = ({ label, name, dateRangeParamName = name }: Props) => {
  const startDateName = `${dateRangeParamName}_start`
  const endDateName = `${dateRangeParamName}_end`
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()

  const [isActive, setActive] = useState(false)

  const [value, setValue] = useState<[Dayjs | null, Dayjs | null] | null>()
  const datePickerRef = useRef<HTMLDivElement>(null)

  useOutsideClick(datePickerRef, () => setActive(false))

  const onClear = () => {
    if (name) {
      setUrlQueryParams({
        [startDateName]: null,
        [endDateName]: null,
      })
    }
    setActive(false)
  }

  const onDateRangeSelect = (dateRange: [Dayjs | null, Dayjs | null]) => {
    const startDate = dateRange[0]?.format("YYYY-MM-DD") || null
    const endDate = dateRange[1]?.format("YYYY-MM-DD") || null

    if (name) {
      setUrlQueryParams({
        [startDateName]: startDate,
        [endDateName]: endDate,
      })
    }

    setActive(false)
  }

  const onCancel = () => {
    setActive(false)
  }

  useEffect(() => {
    if (name && urlQueryParams[startDateName] && urlQueryParams[endDateName]) {
      const startDate = dayjs(urlQueryParams[startDateName])
      const endDate = dayjs(urlQueryParams[endDateName])
      setValue([startDate, endDate])
      return
    } else if (urlQueryParams && endDateName && Object.hasOwn(urlQueryParams, endDateName)) {
      setValue([null, dayjs(urlQueryParams[endDateName])])
      return
    } else if (urlQueryParams && startDateName && Object.hasOwn(urlQueryParams, startDateName)) {
      setValue([dayjs(urlQueryParams[startDateName]), null])
      return
    }

    setValue(null)
  }, [urlQueryParams])

  return (
    <div className={classNames(styles.tableDateWrapper, { [styles.withLabel]: !!label })} onClick={() => {}}>
      {label && (
        <span onClick={() => setActive(true)} className={styles.label}>
          {label}
        </span>
      )}
      {isActive ? (
        <div className={styles.inputWrapper} ref={datePickerRef}>
          <DateRangePicker
            selectedRange={value || undefined}
            onSubmit={onDateRangeSelect}
            onCancel={onCancel}
            allowNulls
            scrollIntoView
          />
        </div>
      ) : value ? (
        <div className={styles.valueWrapper}>
          <span className={styles.value} onClick={() => setActive(true)}>
            {stringifyDateRange(value)}
          </span>
          <span className={styles.icon} onClick={onClear}>
            <IconTableCross />
          </span>
        </div>
      ) : (
        <span className={styles.icon} onClick={() => setActive(true)}>
          <IconCalendar />
        </span>
      )}
    </div>
  )
}
